// M12.1 - GenericSessionView (vista UNIFICADA estilo vrbuzzwire)
//
// Vista de UNA sesion valida para CUALQUIER juego de motricidad. Diseno
// inspirado en la vista original de vrbuzzwire para que todos los juegos
// se vean con la misma elegancia. La vista se adapta al payload del juego:
//
//   - SIEMPRE: 3 KPI cards arriba (Errores totales / Fases completadas /
//     Duracion). Los datos se derivan del summary; si falta alguno se
//     muestra el fallback razonable (—).
//   - SOLO SI HAY DATOS: grafica por fase y mapa de posiciones. Si la
//     experiencia no manda lo suyo, el panel NO SE PINTA. Y si solo
//     sobrevive uno de los dos, ocupa el ancho entero.
//   - SIEMPRE QUE HAYA FASES: lista de fases compacta con duracion y
//     metricas numericas como tags.
//
// LO DE "SOLO SI HAY DATOS" CAMBIO EL 2026-08-11 Y ES LA REGLA DE ESTA
// PANTALLA. Antes esos dos paneles se pintaban siempre, y cuando no habia
// datos ensenaban un recuadro vacio con un texto tecnico —"el juego no envia
// buzzes[].position"— y un enlace a una guia de GitHub. Un mensaje para quien
// programa la experiencia, dentro de la ficha clinica de un paciente. Quien la
// lee no puede hacer nada con el y se lleva la impresion de que la plataforma
// esta a medias.
//
// El aviso al integrador vive donde sirve: en `/revi/telemetry/report/validate`,
// que no toca la base de datos y puede ser todo lo explicito que haga falta.
//
// El detalle de QUE campos espera y como interpretarlos esta en
// `docs/revi-motricity/DASHBOARD_DATA_GUIDE.md`.

import React, { useMemo } from "react";
import { Alert, Card, Col, Empty, Row, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useFormatos } from "../../../../../../i18n/formatos";
import {
  MotricityKpiCard,
  MotricityBarPerPhase,
  MotricityEventScatter,
} from "../_shared";
import { kpisDeSesion } from "../_shared/kpisDeSesion";

// `Paragraph` se usaba solo en el bloque de depuracion que se retiro.
const { Text } = Typography;

function isNumericLike(v) {
  if (typeof v === "number" && Number.isFinite(v)) return true;
  if (typeof v === "string" && v !== "" && !Number.isNaN(Number(v))) {
    return true;
  }
  return false;
}

function humanizeKey(key) {
  return String(key || "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

// Resuelve el nombre humano de la metrica principal del juego.
// Prioridad: summary.totalFaults > game.primaryMetric > "errores totales".
function resolvePrimaryMetricKey(summary, game) {
  if (summary && Object.prototype.hasOwnProperty.call(summary, "totalFaults")) {
    return "totalFaults";
  }
  if (
    game &&
    game.primaryMetric &&
    summary &&
    Object.prototype.hasOwnProperty.call(summary, game.primaryMetric)
  ) {
    return game.primaryMetric;
  }
  return null;
}

// Detecta una metrica numerica presente en TODAS las fases para pintar
// el bar chart "Errores por fase". Prioridad explicita > heuristica.
function resolvePhaseMetric(phases, game, summaryMetricKey, t) {
  if (!Array.isArray(phases) || phases.length === 0) return null;
  // 1. preferir `faults` (convencion vrbuzzwire / jardin)
  if (phases.every((p) => p && isNumericLike(p.faults))) {
    // "Errores" SI es interfaz —la elegimos nosotros, no el juego— y estaba
    // clavada en castellano dentro de un helper sin acceso al diccionario.
    return { key: "faults", label: t("sessionView.metricaErrores") };
  }
  // 2. probar la metrica del summary en cada fase (si game lo replica)
  if (
    summaryMetricKey &&
    phases.every((p) => p && isNumericLike(p[summaryMetricKey]))
  ) {
    return { key: summaryMetricKey, label: humanizeKey(summaryMetricKey) };
  }
  // 3. fallback: primera clave numerica comun (excluye index/duracion)
  const first = phases[0];
  if (!first || typeof first !== "object") return null;
  const candidates = Object.keys(first).filter(
    (k) =>
      k !== "index" &&
      k !== "durationSeconds" &&
      k !== "startedAt" &&
      k !== "finishedAt" &&
      isNumericLike(first[k]),
  );
  for (const key of candidates) {
    if (phases.every((p) => p && isNumericLike(p[key]))) {
      return { key, label: humanizeKey(key) };
    }
  }
  return null;
}

// Extrae puntos posicionados (x, y) de todas las fases. Acepta dos
// convenciones: `phases[].buzzes[].position` y `phases[].events[].position`.
// Proyecta XYZ -> XZ (vista cenital).
function extractPositionedPoints(phases) {
  if (!Array.isArray(phases)) return [];
  const points = [];
  phases.forEach((p) => {
    const list = Array.isArray(p?.buzzes)
      ? p.buzzes
      : Array.isArray(p?.events)
        ? p.events
        : [];
    list.forEach((evt) => {
      const pos = evt && evt.position;
      if (pos && (typeof pos.x === "number" || typeof pos.z === "number")) {
        points.push({ x: Number(pos.x) || 0, y: Number(pos.z) || 0 });
      }
    });
  });
  return points;
}

export function GenericSessionView({ session, game }) {
  // IMPORTANTE: todos los hooks llamados ANTES del primer return para
  // mantener el orden de hooks estable.
  const { t } = useTranslation("motricity");
  const { duracion } = useFormatos();
  const summary = useMemo(() => session?.summary || {}, [session]);
  const phases = useMemo(
    () => (Array.isArray(session?.phases) ? session.phases : []),
    [session],
  );

  const summaryMetricKey = useMemo(
    () => resolvePrimaryMetricKey(summary, game),
    [summary, game],
  );
  // `summaryMetricKey` se sigue usando para resolver la metrica por fase.
  // Lo que se calculaba aqui a partir de el —el valor, el total de fases, las
  // completadas y la duracion— se movio a `kpisDeSesion`, que es quien decide
  // ahora si esos numeros salen del summary del cliente o de la capa que
  // calcula el servidor.

  const phaseMetric = useMemo(
    () => resolvePhaseMetric(phases, game, summaryMetricKey, t),
    [phases, game, summaryMetricKey, t],
  );
  const phaseChartData = useMemo(() => {
    if (!phaseMetric) return [];
    return phases.map((p, i) => ({
      index: p.index ?? i + 1,
      value: Number(p[phaseMetric.key]) || 0,
    }));
  }, [phases, phaseMetric]);

  const positionedPoints = useMemo(
    () => extractPositionedPoints(phases),
    [phases],
  );

  // Los tres KPI de arriba. La funcion decide sola de donde salen: si la
  // sesion trae la capa que calcula el server (`derived.core`), se leen de
  // ahi y significan lo mismo en todas las experiencias; si es una sesion
  // legacy, se sigue adivinando como hasta ahora.
  const { kpis } = useMemo(
    () =>
      kpisDeSesion(session, {
        t,
        duracion: duracion,
        game,
        humanizar: humanizeKey,
      }),
    [session, t, duracion, game],
  );

  // Como termino, cuando NO fue llegando al final. Es informacion clinica que
  // antes se perdia: `completed: false` no distinguia entre dejarlo a medias,
  // quedarse sin tiempo y que la experiencia petara.
  const finalAnomalo =
    session?.derived?.core?.endReason &&
    session.derived.core.endReason !== "finished"
      ? session.derived.core.endReason
      : null;

  if (!session) return <Empty description={t("sessionView.empty")} />;

  // Los paneles que SI tienen algo que ensenar. Se arma la lista antes de
  // pintar para que el ancho de cada uno dependa de cuantos han sobrevivido.
  const panelesConDatos = [];
  if (phaseChartData.length > 0 && phaseMetric) {
    panelesConDatos.push({
      id: "porFase",
      nodo: (
        <MotricityBarPerPhase
          title={t("sessionView.metricaPorFase", {
            metrica: phaseMetric.label,
          })}
          data={phaseChartData}
          yLabel={phaseMetric.label}
          color="#c63f3e"
        />
      ),
    });
  }
  if (positionedPoints.length > 0) {
    panelesConDatos.push({
      id: "posiciones",
      nodo: (
        <MotricityEventScatter
          title={t("sessionView.scatterTitle")}
          points={positionedPoints}
          xLabel="X (m)"
          yLabel="Z (m)"
        />
      ),
    });
  }

  return (
    <div className="motricity-session-view">
      {finalAnomalo ? (
        <Alert
          type={finalAnomalo === "error" ? "error" : "warning"}
          showIcon
          style={{ marginBottom: 12 }}
          message={t(`sessionView.final.${finalAnomalo}`)}
        />
      ) : null}

      {/* KPIs fijos: siempre 3 cards arriba, estilo vrbuzzwire */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {kpis.map((kpi) => (
          <Col xs={24} sm={8} key={kpi.id}>
            <MotricityKpiCard
              title={kpi.title}
              value={kpi.value}
              suffix={kpi.suffix}
              hint={kpi.hint}
              accent={kpi.accent}
            />
          </Col>
        ))}
      </Row>

      {/*
        UN PANEL QUE NO TIENE DATOS NO SE PINTA. NO SE PINTA VACIO.

        Aqui habia dos tarjetas que, cuando la experiencia no mandaba lo suyo,
        se quedaban en pantalla con un recuadro vacio y un texto que decia cosas
        como "el juego no envia buzzes[].position", con un <code> y un enlace a
        una guia de GitHub.

        Eso es un mensaje PARA QUIEN PROGRAMA LA EXPERIENCIA metido en la
        pantalla clinica de un terapeuta. Quien la lee no puede hacer nada con
        ella, no sabe que es un `buzzes[]`, y lo que se lleva es la sensacion de
        que la plataforma esta rota o incompleta. Es exactamente el mismo error
        que el bloque de user agent que se retiro de este fichero el 2026-08-10,
        y por el mismo motivo.

        EL AVISO AL INTEGRADOR NO SE PIERDE, cambia de sitio y mejora: para eso
        esta `/revi/telemetry/report/validate`, que no toca la base de datos y
        por tanto puede ser todo lo explicito que haga falta —dice campo a campo
        que falta y que se calcularia—. Un endpoint de ensayo es mejor sitio
        para eso que la ficha de un paciente.

        Y si solo sobrevive uno de los dos, ocupa el ancho entero: dejar media
        fila en blanco vuelve a contar la misma historia de "aqui falta algo".
      */}
      {panelesConDatos.length > 0 ? (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {panelesConDatos.map((panel) => (
            <Col
              xs={24}
              lg={panelesConDatos.length === 1 ? 24 : 12}
              key={panel.id}
            >
              {panel.nodo}
            </Col>
          ))}
        </Row>
      ) : null}

      {/* Detalle de fases en lista compacta (mantiene la info de Jardin/
          Mariposa/Hojas pero estilo unificado) */}
      {phases.length > 0 ? (
        <Card
          title={t("sessionView.fasesTitulo", { count: phases.length })}
          variant="outlined"
        >
          {phases.map((p, i) => {
            const otherEntries = Object.entries(p).filter(
              ([k, v]) =>
                k !== "index" &&
                k !== "durationSeconds" &&
                k !== "startedAt" &&
                k !== "finishedAt" &&
                k !== "buzzes" &&
                k !== "events" &&
                isNumericLike(v),
            );
            return (
              <div
                key={p.index ?? i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <Tag color="blue">
                    {t("sessionView.faseNumero", { numero: p.index ?? i + 1 })}
                  </Tag>
                  <Text type="secondary" style={{ marginLeft: 4 }}>
                    {duracion(Number(p.durationSeconds || 0))}
                  </Text>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {otherEntries.slice(0, 4).map(([k, v]) => (
                    <Tag key={k}>
                      {humanizeKey(k)}: <strong>{Number(v)}</strong>
                    </Tag>
                  ))}
                </div>
              </div>
            );
          })}
        </Card>
      ) : null}

      {/*
        RETIRADO el 2026-08-10: aqui se pintaba el user agent del navegador, su
        idioma y si era VR. El propio comentario decia "debug discreto".

        Era informacion de diagnostico tecnico en la pantalla CLINICA de un
        terapeuta, que no puede hacer nada con ella y a quien solo le anade
        ruido en la ficha de un paciente. El dato se sigue guardando en la
        sesion: quien lo necesite para diagnosticar un problema lo tiene en el
        documento, que es donde le sirve de algo.
      */}
    </div>
  );
}
